import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import { calculateTotalLOS } from "..//calculateTotalLOS";
import { calculateTotalADR } from "../calculateTotalADR";
import { calculateTotalLeadTime } from "../calculateTotalLeadTime";
import { calculateTotalRoomNights } from "../calculateTotalRoomNights";
import { calculateYoYChange } from "../calculateYoYChange";
import { formatADRChartData } from "../formatADRChartData";
import { formatBookingsChartData } from "../formatBookingsChartData";
import { formatLOSChartData } from "../formatLOSChartData";
import { formatLeadTimeChartData } from "../formatLeadTimeChartData";
import { formatNationalityChartData } from "../formatNationalityChartData";
import { formatPaymentMethodChartData } from "../formatPaymentMethodChartData";
import { formatPromotionChartData } from "../formatPromotionChartData";
import { formatRatePlanChartData } from "../formatRatePlanChartData";
import { formatRevenueChartData } from "../formatRevenueChartData";
import { formatRoomNightsChartData } from "../formatRoomNightsChartData";
import { formatRoomTypeChartData } from "../formatRoomTypeChartData";
import { getYoYTrend } from "../getYoYTrend";

export const exportToExcel = (
  bookedBookings,
  cancelledBookings,
  dataMode,
  dateRange,
  previousBookedBookings,
  previousCancelledBookings
) => {
  const bookingsChartData = formatBookingsChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const roomNightsChartData = formatRoomNightsChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const revenueChartData = formatRevenueChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const adrChartData = formatADRChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const losChartData = formatLOSChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const leadTimeChartData = formatLeadTimeChartData(
    bookedBookings,
    cancelledBookings,
    dataMode
  );
  const roomTypeChartData = formatRoomTypeChartData(
    bookedBookings,
    cancelledBookings
  );
  const promotionChartData = formatPromotionChartData(
    bookedBookings,
    cancelledBookings
  );
  const ratePlanChartData = formatRatePlanChartData(
    bookedBookings,
    cancelledBookings
  );
  const paymentMethodChartData = formatPaymentMethodChartData(
    bookedBookings,
    cancelledBookings
  );
  const bookedNationalityChartData = formatNationalityChartData(
    bookedBookings,
    "Booked Nationalities"
  );
  const cancelledNationalityChartData = formatNationalityChartData(
    cancelledBookings,
    "Cancelled Nationalities"
  );

  // Current year metrics
  const totalBookingsBooked = bookedBookings.length;
  const totalBookingsCancelled = cancelledBookings.length;

  const totalRoomNightsBooked = calculateTotalRoomNights(bookedBookings);
  const totalRoomNightsCancelled = calculateTotalRoomNights(cancelledBookings);

  const totalRevenueBooked = bookedBookings.reduce(
    (sum, booking) => sum + booking.totalamount,
    0
  );
  const totalRevenueCancelled = cancelledBookings.reduce(
    (sum, booking) => sum + booking.totalamount,
    0
  );
  const totalADRBooked = calculateTotalADR(bookedBookings);
  const totalADRCancelled = calculateTotalADR(cancelledBookings);
  const totalLOSBooked = calculateTotalLOS(bookedBookings);
  const totalLOSCancelled = calculateTotalLOS(cancelledBookings);
  const totalLeadTimeBooked = calculateTotalLeadTime(bookedBookings);
  const totalLeadTimeCancelled = calculateTotalLeadTime(cancelledBookings);

  // Previous year metrics
  const previousTotalBookingsBooked = previousBookedBookings.length;
  const previousTotalBookingsCancelled = previousCancelledBookings.length;

  const previousTotalRoomNightsBooked = calculateTotalRoomNights(
    previousBookedBookings
  );
  const previousTotalRoomNightsCancelled = calculateTotalRoomNights(
    previousCancelledBookings
  );

  const previousTotalRevenueBooked = previousBookedBookings.reduce(
    (sum, booking) => sum + booking.totalamount,
    0
  );
  const previousTotalRevenueCancelled = previousCancelledBookings.reduce(
    (sum, booking) => sum + booking.totalamount,
    0
  );
  const previousTotalADRBooked = calculateTotalADR(previousBookedBookings);
  const previousTotalADRCancelled = calculateTotalADR(
    previousCancelledBookings
  );
  const previousTotalLOSBooked = calculateTotalLOS(previousBookedBookings);
  const previousTotalLOSCancelled = calculateTotalLOS(
    previousCancelledBookings
  );
  const previousTotalLeadTimeBooked = calculateTotalLeadTime(
    previousBookedBookings
  );
  const previousTotalLeadTimeCancelled = calculateTotalLeadTime(
    previousCancelledBookings
  );

  // YoY Trends
  const trendBookingsBooked = getYoYTrend(
    totalBookingsBooked,
    previousTotalBookingsBooked
  );
  const trendBookingsCancelled = getYoYTrend(
    totalBookingsCancelled,
    previousTotalBookingsCancelled
  );
  const trendRoomNightsBooked = getYoYTrend(
    totalRoomNightsBooked,
    previousTotalRoomNightsBooked
  );
  const trendRoomNightsCancelled = getYoYTrend(
    totalRoomNightsCancelled,
    previousTotalRoomNightsCancelled
  );
  const trendRevenueBooked = getYoYTrend(
    totalRevenueBooked,
    previousTotalRevenueBooked
  );
  const trendRevenueCancelled = getYoYTrend(
    totalRevenueCancelled,
    previousTotalRevenueCancelled
  );
  const trendADRBooked = getYoYTrend(totalADRBooked, previousTotalADRBooked);
  const trendADRCancelled = getYoYTrend(
    totalADRCancelled,
    previousTotalADRCancelled
  );
  const trendLOSBooked = getYoYTrend(totalLOSBooked, previousTotalLOSBooked);
  const trendLOSCancelled = getYoYTrend(
    totalLOSCancelled,
    previousTotalLOSCancelled
  );
  const trendLeadTimeBooked = getYoYTrend(
    totalLeadTimeBooked,
    previousTotalLeadTimeBooked
  );
  const trendLeadTimeCancelled = getYoYTrend(
    totalLeadTimeCancelled,
    previousTotalLeadTimeCancelled
  );

  // YoY Changes
  const yoyBookingsBooked = calculateYoYChange(
    totalBookingsBooked,
    previousTotalBookingsBooked
  );
  const yoyBookingsCancelled = calculateYoYChange(
    totalBookingsCancelled,
    previousTotalBookingsCancelled
  );
  const yoyRoomNightsBooked = calculateYoYChange(
    totalRoomNightsBooked,
    previousTotalRoomNightsBooked
  );
  const yoyRoomNightsCancelled = calculateYoYChange(
    totalRoomNightsCancelled,
    previousTotalRoomNightsCancelled
  );
  const yoyRevenueBooked = calculateYoYChange(
    totalRevenueBooked,
    previousTotalRevenueBooked
  );
  const yoyRevenueCancelled = calculateYoYChange(
    totalRevenueCancelled,
    previousTotalRevenueCancelled
  );
  const yoyADRBooked = calculateYoYChange(
    totalADRBooked,
    previousTotalADRBooked
  );
  const yoyADRCancelled = calculateYoYChange(
    totalADRCancelled,
    previousTotalADRCancelled
  );
  const yoyLOSBooked = calculateYoYChange(
    totalLOSBooked,
    previousTotalLOSBooked
  );
  const yoyLOSCancelled = calculateYoYChange(
    totalLOSCancelled,
    previousTotalLOSCancelled
  );
  const yoyLeadTimeBooked = calculateYoYChange(
    totalLeadTimeBooked,
    previousTotalLeadTimeBooked
  );
  const yoyLeadTimeCancelled = calculateYoYChange(
    totalLeadTimeCancelled,
    previousTotalLeadTimeCancelled
  );

  const workbook = XLSX.utils.book_new();

  const chartDataArray = [
    { title: "Bookings Over Time", data: bookingsChartData },
    { title: "Room Nights Over Time", data: roomNightsChartData },
    { title: "Revenue Over Time", data: revenueChartData },
    { title: "ADR Over Time", data: adrChartData },
    { title: "LOS Over Time", data: losChartData },
    { title: "Lead Time Over Time", data: leadTimeChartData },
    { title: "Room Types", data: roomTypeChartData },
    { title: "Promotions", data: promotionChartData },
    { title: "Rate Plans", data: ratePlanChartData },
    { title: "Payment Methods", data: paymentMethodChartData },
    { title: "Booked Nationalities", data: bookedNationalityChartData },
    { title: "Cancelled Nationalities", data: cancelledNationalityChartData },
  ];

  chartDataArray.forEach((chart) => {
    const { title, data } = chart;
    const worksheetData = [];

    const headers = ["Label", ...data.datasets.map((ds) => ds.label)];
    worksheetData.push(headers);

    data.labels.forEach((label, index) => {
      const row = [label];
      data.datasets.forEach((ds) => {
        row.push(ds.data[index] || 0);
      });
      worksheetData.push(row);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, title.slice(0, 31));
  });

  const summaryData = [
    ["Booking Analytics Summary", "", ""],
    [
      "Date Range",
      `${dateRange[0]?.toLocaleDateString("vi-VN") || "N/A"} to ${
        dateRange[1]?.toLocaleDateString("vi-VN") || "N/A"
      }`,
      "",
    ],
    ["Data Mode", dataMode, ""],
    [
      "Metric",
      "Current Year (Booked / Cancelled)",
      "Previous Year (Booked / Cancelled) and YoY Change",
    ],
    [
      "Total Bookings",
      `${totalBookingsBooked.toLocaleString(
        "vi-VN"
      )} / ${totalBookingsCancelled.toLocaleString("vi-VN")}`,
      previousTotalBookingsBooked === 0 && previousTotalBookingsCancelled === 0
        ? "No data for previous year"
        : `${previousTotalBookingsBooked.toLocaleString(
            "vi-VN"
          )} / ${previousTotalBookingsCancelled.toLocaleString(
            "vi-VN"
          )} (${trendBookingsBooked} ${
            yoyBookingsBooked.percentage
          } / ${trendBookingsCancelled} ${yoyBookingsCancelled.percentage})`,
    ],
    [
      "Total Room Nights",
      `${totalRoomNightsBooked.toLocaleString(
        "vi-VN"
      )} / ${totalRoomNightsCancelled.toLocaleString("vi-VN")}`,
      previousTotalRoomNightsBooked === 0 &&
      previousTotalRoomNightsCancelled === 0
        ? "No data for previous year"
        : `${previousTotalRoomNightsBooked.toLocaleString(
            "vi-VN"
          )} / ${previousTotalRoomNightsCancelled.toLocaleString(
            "vi-VN"
          )} (${trendRoomNightsBooked} ${
            yoyRoomNightsBooked.percentage
          } / ${trendRoomNightsCancelled} ${
            yoyRoomNightsCancelled.percentage
          })`,
    ],
    [
      "Total Revenue",
      `${totalRevenueBooked.toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      })} / ${totalRevenueCancelled.toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      })}`,
      previousTotalRevenueBooked === 0 && previousTotalRevenueCancelled === 0
        ? "No data for previous year"
        : `${previousTotalRevenueBooked.toLocaleString("vi-VN", {
            style: "currency",
            currency: "VND",
          })} / ${previousTotalRevenueCancelled.toLocaleString("vi-VN", {
            style: "currency",
            currency: "VND",
          })} (${trendRevenueBooked} ${
            yoyRevenueBooked.percentage
          } / ${trendRevenueCancelled} ${yoyRevenueCancelled.percentage})`,
    ],
    [
      "Total ADR",
      `${Number(totalADRBooked).toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      })} / ${Number(totalADRCancelled).toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      })}`,
      previousTotalADRBooked === 0 && previousTotalADRCancelled === 0
        ? "No data for previous year"
        : `${Number(previousTotalADRBooked).toLocaleString("vi-VN", {
            style: "currency",
            currency: "VND",
          })} / ${Number(previousTotalADRCancelled).toLocaleString("vi-VN", {
            style: "currency",
            currency: "VND",
          })} (${trendADRBooked} ${
            yoyADRBooked.percentage
          } / ${trendADRCancelled} ${yoyADRCancelled.percentage})`,
    ],
    [
      "Total LOS",
      `${Number(totalLOSBooked).toLocaleString("vi-VN")} / ${Number(
        totalLOSCancelled
      ).toLocaleString("vi-VN")} Days`,
      previousTotalLOSBooked === 0 && previousTotalLOSCancelled === 0
        ? "No data for previous year"
        : `${Number(previousTotalLOSBooked).toLocaleString("vi-VN")} / ${Number(
            previousTotalLOSCancelled
          ).toLocaleString("vi-VN")} Days (${trendLOSBooked} ${
            yoyLOSBooked.percentage
          } / ${trendLOSCancelled} ${yoyLOSCancelled.percentage})`,
    ],
    [
      "Avg Lead Time",
      `${Number(totalLeadTimeBooked).toLocaleString("vi-VN")} / ${Number(
        totalLeadTimeCancelled
      ).toLocaleString("vi-VN")} Days`,
      previousTotalLeadTimeBooked === 0 && previousTotalLeadTimeCancelled === 0
        ? "No data for previous year"
        : `${Number(previousTotalLeadTimeBooked).toLocaleString(
            "vi-VN"
          )} / ${Number(previousTotalLeadTimeCancelled).toLocaleString(
            "vi-VN"
          )} Days (${trendLeadTimeBooked} ${
            yoyLeadTimeBooked.percentage
          } / ${trendLeadTimeCancelled} ${yoyLeadTimeCancelled.percentage})`,
    ],
  ];

  const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(
    data,
    `Booking_Analytics_${
      dateRange[0]?.toLocaleDateString("vi-VN") || "N/A"
    }_to_${dateRange[1]?.toLocaleDateString("vi-VN") || "N/A"}.xlsx`
  );
};
